import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import compose from 'recompose/compose'
import searchModalDuck from 'components/search/search-section/duck'
import { HEADER_SEARCH_FORM_ID } from 'components/search/search-form'
import {
  getRecentSearchItems,
  getSearchBarMargin,
  openSearchResults,
  storeTheSearchWord,
} from 'components/search/utils'
import { withI18next } from 'lib/i18n/withI18next'
import InPageSearchResults from '../in-page-results'
import SearchSuggestions from '../search-suggestions'
import SearchSidebar from './search-sidebar'
import styles from './style.scss'

const DROPDOWN_MIN_WIDTH = 480

// TECH-20: the whole header (banner, nav, logo, search box) stays visible on
// search; the grey layer, the dropdown and the results panel all start at the
// header's measured bottom. Measuring (rather than a fixed 60/54px) means a
// banner pushing the header down can never leave the overlay over the header.
// The dropdown also aligns under the box on desktop, ASOS style.
const FALLBACK_TOP = { phone: 54, desktop: 60 }

const getSearchLayout = (isPhone) => {
  // There can be more than one #header-search-form in the DOM (the desktop inline
  // box and a mobile input share the id). Measure the one that is actually visible,
  // otherwise the dropdown aligns to a hidden / zero-width box and lands on the left.
  const forms =
    typeof document !== 'undefined'
      ? Array.from(document.querySelectorAll(`#${HEADER_SEARCH_FORM_ID}`))
      : []
  const form =
    forms.find((el) => el.getBoundingClientRect().width > 0) || forms[0]
  if (!form) {
    const top = isPhone ? FALLBACK_TOP.phone : FALLBACK_TOP.desktop
    return { backdrop: { top, height: `calc(100vh - ${top}px)` }, dropdown: {} }
  }

  const box = form.getBoundingClientRect()
  const header = form.closest('header')
  // bottom of the whole header, or at least the search box
  const bottom = Math.max(
    box.bottom,
    header ? header.getBoundingClientRect().bottom : 0,
  )
  const top = Math.max(0, Math.round(bottom))
  const backdrop = { top, height: `calc(100vh - ${top}px)` }

  if (isPhone) {
    return { backdrop, dropdown: {} }
  }

  const dropdownWidth = Math.max(box.width, DROPDOWN_MIN_WIDTH)
  // keep the dropdown aligned under the box but inside the viewport
  const maxLeft = window.innerWidth - dropdownWidth - 16
  const alignedLeft = box.right - dropdownWidth
  return {
    backdrop,
    dropdown: {
      marginLeft: Math.round(Math.max(16, Math.min(alignedLeft, maxLeft))),
      width: dropdownWidth,
    },
  }
}

const SearchSection = ({
  closeSearchModal,
  isPhone,
  openResults,
  query,
  searchWord,
  setSearchWord,
}) => {
  const baseSearchBarMargin = getSearchBarMargin()
  const [searchBarMargin, setSearchBarMargin] = useState(baseSearchBarMargin)
  const [layout, setLayout] = useState(() => getSearchLayout(isPhone))

  const updateLayout = useCallback(() => {
    setSearchBarMargin(getSearchBarMargin())
    setLayout(getSearchLayout(isPhone))
  }, [isPhone])

  useEffect(() => {
    updateLayout()
    window.addEventListener('resize', updateLayout)

    return () => {
      window.removeEventListener('resize', updateLayout)
    }
  }, [updateLayout])

  const headerSideMargin = searchBarMargin?.split(' ')?.[1]
  const parsedSideMargin = headerSideMargin && parseFloat(headerSideMargin)

  // TECH-20: a suggestion runs that search in place, like a typed one
  const searchSuggestion = (suggestion) => {
    storeTheSearchWord(suggestion, setSearchWord)
    openSearchResults(suggestion, openResults)
  }
  const isShowingTypedSearch =
    Boolean(query) && (searchWord || '').trim() === query.trim()

  // Before a search runs - and whenever the shopper edits the query again over
  // its results - the page stays under a grey layer and the choices sit in a
  // dropdown aligned under the search box (ASOS). Only the committed, typed
  // search fills the results panel. This keeps the suggestions + product preview
  // under the box in both cases, never pinned to the left of the screen.
  if (!isShowingTypedSearch) {
    const typed = (searchWord || '').trim()
    const hasRecent = (getRecentSearchItems() || []).length > 0
    const showDropdown = hasRecent || typed.length >= 2

    return (
      <div
        className="search-section__wrapper search-section__wrapper--dropdown"
        data-cy="search-dropdown-backdrop"
        style={layout.backdrop}
        onClick={closeSearchModal}
        role="presentation"
      >
        <style jsx>{styles}</style>
        {showDropdown && (
          <div
            className="search-section__dropdown"
            style={layout.dropdown}
            onClick={(e) => e.stopPropagation()}
            role="presentation"
          >
            <SearchSuggestions
              term={searchWord || ''}
              onSelect={searchSuggestion}
              isPhone={isPhone}
            >
              <SearchSidebar setSearchWord={setSearchWord} />
            </SearchSuggestions>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="search-section__wrapper" style={layout.backdrop}>
      <style jsx>{styles}</style>
      <div
        className="search-section__container"
        style={{ paddingLeft: parsedSideMargin }}
      >
        <InPageSearchResults key={query} query={query} />
      </div>
    </div>
  )
}

SearchSection.defaultProps = {
  isPhone: false,
  query: '',
  searchWord: '',
}

SearchSection.propTypes = {
  closeSearchModal: PropTypes.func.isRequired,
  isPhone: PropTypes.bool,
  openResults: PropTypes.func.isRequired,
  query: PropTypes.string,
  searchWord: PropTypes.string,
  setSearchWord: PropTypes.func.isRequired,
}

export default compose(
  connect(
    (state) => ({
      isPhone: state.device.isPhone,
      query: state.searchModal.query,
    }),
    {
      closeSearchModal: searchModalDuck.creators.closeSearchModal,
      openResults: searchModalDuck.creators.openSearchResults,
    },
  ),
  withI18next(),
)(SearchSection)
